import { PassiveTooltip } from '@systemeio/components'
import { CheckIcon, Copy03Icon } from '@systemeio/icons'
import { useEffect, useRef, useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { copyTextToClipboard } from 'shared/utils/copy-text-to-clipboard'
import { twMerge } from 'tailwind-merge'

interface CopyInputValueButtonProps {
  className?: string
  disabled?: boolean
  onCopy?: () => void
  text: string
}

function CopyInputValueButton({ className, disabled, onCopy, text }: CopyInputValueButtonProps) {
  const { t } = useLocoTranslation()
  const [isCopied, setIsCopied] = useState(false)
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(
    () => () => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current)
    },
    [],
  )

  const handleCopy = async () => {
    const copied = await copyTextToClipboard(text, false)
    if (!copied) {
      setIsCopied(false)
      return
    }

    onCopy?.()
    setIsCopied(true)
    if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current)
    copiedTimeoutRef.current = setTimeout(() => setIsCopied(false), 1500)
  }

  return (
    <PassiveTooltip
      content={isCopied ? t('global.copy_clipboard') : t('global.copy')}
      trigger={
        isCopied ? (
          <CheckIcon aria-hidden="true" className="text-icon-accent size-4" />
        ) : (
          <Copy03Icon
            aria-hidden="true"
            className="text-icon-secondary hover:text-icon-accent transition-base size-4"
          />
        )
      }
      positionerProps={{ side: 'bottom' }}
      triggerProps={{
        // Base UI closes a tooltip on press; here the press is the copy and the tooltip confirms it.
        closeOnClick: false,
        render: (
          <button
            aria-label={t('global.copy')}
            className={twMerge(
              'focus-visible:[&_svg]:text-icon-accent focus-visible:focus-ring-primary outline-none',
              className,
            )}
            disabled={disabled}
            onClick={() => void handleCopy()}
            type="button"
          />
        ),
      }}
    />
  )
}

export { CopyInputValueButton }
export type { CopyInputValueButtonProps }
